// SPDX-License-Identifier: LicenseRef-Techsafe-Proprietary
// Copyright (c) 2026 Techsafe Teknoloji ve Muhendislik A.S.
// https://techsafe-solutions.com
// Economic rights: Techsafe A.S.
// Authors' moral rights reserved under Turkish Copyright Law (FSEK 5846).
// Mali haklar Techsafe A.S.'ye aittir; eser sahiplerinin manevi haklari saklidir (FSEK 5846).
// This content was prepared with the assistance of AI tools, reviewed by a
// human, and published by Techsafe Teknoloji ve Muhendislik A.S. The publishing
// entity is responsible for accuracy and compliance.
// Bu icerik, yapay zeka destekli araclar kullanilarak hazirlanmis, insan
// incelemesinden gecirilmis ve Techsafe Teknoloji ve Muhendislik A.S.
// tarafindan yayimlanmistir. Icerigin dogrulugundan yayimlayan kurulus
// sorumludur.
//
// ADR-003 D1: one search box, auto-detected identifier type. The detected type
// is shown only as a hint (convenience, not a gate — ADR-003 D2). We do NOT use
// an HTML <form>: interactions run through explicit handlers.

import { Clock, Search, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { guessIdentifierType } from "./identifier";
import { useRecentSearches } from "./use-recent-searches";
import { cn } from "@/lib/utils";
import { Badge } from "@/primitives/badge";
import { Button } from "@/primitives/button";
import { Input } from "@/primitives/input";

export interface SearchBoxProps {
  disabled?: boolean;
  onSearch: (identifier: string) => void;
  /**
   * Anchor for the recent-searches dropdown. Defaults to "below" (today's
   * behavior). Use "above" when SearchBox sits in a bottom-pinned container
   * (e.g. NCActionFooter), where a below-anchored dropdown would render
   * off-screen.
   */
  dropdownPosition?: "below" | "above";
}

export function SearchBox({
  disabled = false,
  onSearch,
  dropdownPosition = "below",
}: SearchBoxProps) {
  const [value, setValue] = useState("");
  const [focused, setFocused] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const blurTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  const recent = useRecentSearches();
  const trimmed = value.trim();
  const canSearch = trimmed.length > 0 && !disabled;
  const guess = trimmed.length > 0 ? guessIdentifierType(trimmed) : null;

  // Filter recent searches by typed value (case-insensitive substring).
  const matches = useMemo(() => {
    if (!focused) return [];
    const q = value.trim().toLowerCase();
    if (!q) return recent.items;
    return recent.items.filter((item) => item.toLowerCase().includes(q));
  }, [focused, value, recent.items]);

  const runSearch = useCallback(
    (identifier: string) => {
      const id = identifier.trim();
      if (!id || disabled) return;
      setValue(id);
      recent.add(id);
      onSearch(id);
      setFocused(false);
    },
    [disabled, recent, onSearch],
  );

  const submit = useCallback(() => {
    if (!canSearch) return;
    // If an item is active in the dropdown, pick it.
    if (activeIndex >= 0 && activeIndex < matches.length) {
      runSearch(matches[activeIndex]!);
      return;
    }
    runSearch(trimmed);
  }, [canSearch, activeIndex, matches, runSearch, trimmed]);

  // Reset active index when matches change.
  useEffect(() => {
    setActiveIndex(-1);
  }, [matches]);

  // Scroll active item into view.
  useEffect(() => {
    if (activeIndex < 0 || !listRef.current) return;
    const item = listRef.current.children[activeIndex] as HTMLElement | undefined;
    item?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  // Delayed blur so clicks on dropdown items register before the dropdown hides.
  function handleFocus() {
    clearTimeout(blurTimer.current);
    setFocused(true);
  }

  function handleBlur() {
    blurTimer.current = setTimeout(() => setFocused(false), 150);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Escape") {
      setFocused(false);
      inputRef.current?.blur();
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) => (prev < matches.length - 1 ? prev + 1 : 0));
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) => (prev > 0 ? prev - 1 : matches.length - 1));
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      submit();
    }
  }

  const showDropdown = focused && matches.length > 0;
  const dropdownAnchorClasses =
    dropdownPosition === "above" ? "bottom-full mb-1" : "top-full mt-1";

  return (
    // One row, laid out in columns (input | detected-type | action) — the
    // label above it and the "detected as…" hint below it used to each take
    // their own row, stacking the bar three deep. The label is accessible-only
    // now (the placeholder already carries it visually); the hint collapses
    // into a compact inline badge that only appears once there's a real guess.
    <div className="flex w-full items-center gap-nc-2">
      <label htmlFor="au0009-search" className="sr-only">
        Serial number or software ID
      </label>
      <div className="relative min-w-0 flex-1">
          <Search
            className="pointer-events-none absolute left-nc-3 top-1/2 z-10 size-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            ref={inputRef}
            id="au0009-search"
            value={value}
            disabled={disabled}
            placeholder="e.g. SN-1001 or 8f14e45f-ceea-467d-9f0c-1b2a3c4d5e6f"
            className={cn("pl-9", guess ? "pr-36" : "pr-9")}
            onChange={(e) => setValue(e.target.value)}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            autoComplete="off"
            role="combobox"
            aria-expanded={showDropdown}
            aria-controls="au0009-search-listbox"
            aria-activedescendant={
              activeIndex >= 0 ? `au0009-suggestion-${activeIndex}` : undefined
            }
          />

          {/* Trailing, inside the input's own border: the detected-type hint
              (a self-contained "Detected as …" chip, ADR-003 D2) and the
              clear button share this slot instead of the hint sitting beside
              the input as a separate element. */}
          <div className="absolute right-nc-2 top-1/2 z-10 flex -translate-y-1/2 items-center gap-nc-1">
            {guess ? (
              <Badge tone="info" className="hidden shrink-0 whitespace-nowrap sm:inline-flex">
                Detected as {guess === "software-id" ? "software ID" : "serial number"}
              </Badge>
            ) : null}
            {value.length > 0 ? (
              <button
                type="button"
                aria-label="Clear search"
                onClick={() => {
                  setValue("");
                  setActiveIndex(-1);
                  inputRef.current?.focus();
                }}
                className="inline-flex size-5 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                <X className="size-3.5" />
              </button>
            ) : null}
          </div>

          {/* Dropdown */}
          {showDropdown ? (
            <ul
              ref={listRef}
              id="au0009-search-listbox"
              role="listbox"
              className={`absolute left-0 right-0 z-20 ${dropdownAnchorClasses} max-h-60 overflow-auto rounded-md border border-border bg-popover p-1 shadow-md`}
            >
              {matches.map((item, idx) => (
                <li
                  key={item}
                  id={`au0009-suggestion-${idx}`}
                  role="option"
                  aria-selected={idx === activeIndex}
                  onMouseDown={(e) => {
                    // Prevent blur from hiding the dropdown before click fires.
                    e.preventDefault();
                    runSearch(item);
                  }}
                  onMouseEnter={() => setActiveIndex(idx)}
                  className={`flex cursor-pointer items-center gap-nc-2 rounded-sm px-nc-2 py-nc-1.5 text-sm ${
                    idx === activeIndex
                      ? "bg-accent text-accent-foreground"
                      : "text-foreground hover:bg-muted"
                  }`}
                >
                  <Clock className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
                  <span className="flex-1 truncate">{item}</span>
                  <button
                    type="button"
                    aria-label={`Remove ${item}`}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      recent.remove(item);
                    }}
                    className="inline-flex size-4 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-muted-foreground/20 hover:text-foreground"
                  >
                    <X className="size-3" />
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
      </div>

      <Button disabled={!canSearch} onClick={submit} className="shrink-0">
        Show status
      </Button>
    </div>
  );
}
